/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit, signal} from '@angular/core';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import * as _ from 'lodash';

import template from './change-password-modal.component.html';
import {AdnTranslateService} from '../../common/filters/adn-translate.service';
import {AuthenticatedUserService} from '../../common/controller/authenticated-user.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, ButtonsModule],
  selector: 'adn-change-password-modal',
  template
})
export class ChangePasswordModalComponent implements OnInit {
  readonly onCompleted = new Subject<void>();

  model: any = {};
  private readonly errorState = signal<any>(null);
  private readonly passwordChangedState = signal(false);
  private readonly isSubmittingState = signal(false);
  private readonly sendingEmailCodeState = signal(false);
  private readonly isEmail2faState = signal(false);
  private readonly codeSentState = signal(false);
  private readonly fieldsDisabledState = signal(false);

  get error(): any { return this.errorState(); }
  set error(value: any) { this.errorState.set(value); }
  get passwordChanged(): boolean { return this.passwordChangedState(); }
  set passwordChanged(value: boolean) { this.passwordChangedState.set(value); }
  get isSubmitting(): boolean { return this.isSubmittingState(); }
  set isSubmitting(value: boolean) { this.isSubmittingState.set(value); }
  get sendingEmailCode(): boolean { return this.sendingEmailCodeState(); }
  set sendingEmailCode(value: boolean) { this.sendingEmailCodeState.set(value); }
  get isEmail2fa(): boolean { return this.isEmail2faState(); }
  set isEmail2fa(value: boolean) { this.isEmail2faState.set(value); }
  get codeSent(): boolean { return this.codeSentState(); }
  set codeSent(value: boolean) { this.codeSentState.set(value); }
  /** Stable [disabled] flag — avoids NG0100 when codeSent flips mid-refresh. */
  get fieldsDisabled(): boolean { return this.fieldsDisabledState(); }
  set fieldsDisabled(value: boolean) { this.fieldsDisabledState.set(value); }

  private _user: any;

  /** Set via BsModalService initialState — sync flags before first CD. */
  set user(value: any) {
    this._user = value;
    this.isEmail2fa = value?.twoFactorAuthType !== 'APP' || value?.login2faRequired !== true;
    this.syncFieldsDisabled();
  }

  get user(): any {
    return this._user;
  }

  constructor(
    public bsModalRef: BsModalRef,
    private readonly adnTranslate: AdnTranslateService,
    private readonly authenticatedUserResource: AuthenticatedUserService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any
  ) {
  }

  ngOnInit(): void {
    this.syncFieldsDisabled();
  }

  translateError(err: {text?: string; code?: string; parameters?: any}): string {
    if (!err) {
      return '';
    }
    return this.adnTranslate.transform(err.text, err.code, err.parameters);
  }

  sendEmailCode(): void {
    this.sendingEmailCode = true;
    this.codeSent = false;
    this.syncFieldsDisabled();
    this.authenticatedUserResource.sendEmail2faCode('change-password').then((result: any) => {
      deferSignalStateWrite(() => {
        if (result === true) {
          this.codeSent = true;
          this.error = null;
        } else {
          this.error = result.data;
        }
        this.sendingEmailCode = false;
        this.syncFieldsDisabled();
      });
    }).catch((result: any) => {
      deferSignalStateWrite(() => {
        this.error = _.get(result, ['data']) || result;
        this.sendingEmailCode = false;
        this.syncFieldsDisabled();
      });
    });
  }

  changePassword(): void {
    if (!this.model.password) {
      this.error = {text: 'Your current password is required'};
      return;
    }

    if (!this.model.twoFactorAuthCode || this.model.twoFactorAuthCode.length < 6) {
      if (this.isEmail2fa) {
        this.error = {text: this.model.twoFactorAuthCode && this.model.twoFactorAuthCode.length < 6
          ? 'Your verification code should be at least 6 characters long'
          : 'Your verification code is required'};
      } else {
        this.error = {text: this.model.twoFactorAuthCode && this.model.twoFactorAuthCode.length < 6
          ? 'Your 2FA code should be at least 6 characters long'
          : 'Your 2FA code is required'};
      }
      return;
    }

    if (!this.model.newPassword || this.model.newPassword.length < 8) {
      this.error = {text: 'Your new password needs to be at least 8 characters long'};
      return;
    }

    if (this.model.newPassword !== this.model.confirmPassword) {
      this.error = {text: 'The passwords you entered do not match. Ensure you\'ve entered the same password both times.'};
      return;
    }

    this.error = null;
    this.isSubmitting = true;
    this.authenticatedUserResource.changePassword(
      this.model.password,
      this.model.newPassword,
      this.model.twoFactorAuthCode
    ).then((response: any) => {
      deferSignalStateWrite(() => {
        if (response.state === true) {
          this.localUserProfile.set('passwordExpiry', response.data.passwordExpiry);
          this.localUserProfile.set('passwordExpiresDays', response.data.passwordExpiresDays);
          this.passwordChanged = true;
          this.error = null;
        } else if (response.code === 'error.auth.invalid.two.factor.auth.token') {
          this.error = {text: this.isEmail2fa
            ? 'The Verification Code you provided is invalid. Please try again'
            : 'The 2FA code you provided is invalid. Please try again'};
        } else {
          this.error = response;
        }
        this.isSubmitting = false;
      });
    }).catch((response: any) => {
      deferSignalStateWrite(() => {
        this.error = _.get(response, ['data']) || response;
        this.isSubmitting = false;
      });
    });
  }

  close(): void {
    this.onCompleted.next();
    this.bsModalRef.hide();
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  private syncFieldsDisabled(): void {
    this.fieldsDisabled = this.isEmail2fa && !this.codeSent;
  }
}
